import type {Class, SubClass} from '../../../generated/types';
import type {RouteName} from '../../../lib/routes';
import {Abilities} from '../../../components/enums';
import {humanEnumeration} from '../../../components/utils';
import {composeDescription} from '../../../lib/seo/text';
import type {OgCardData} from '../../../lib/seo/site';

export type ClassSection = {id: string; type: string; title: string; description?: string | null};

/** Разделы класса (воззвания, манёвры…): маршрут списка и маршрут отдельного пункта */
export const SECTION_ROUTES: Record<string, {list: RouteName; feature: RouteName}> = {
	Metamagic: {list: 'classMetaMagics', feature: 'classMetaMagicFeature'},
	Invocation: {list: 'classInvocations', feature: 'classInvocationFeature'},
	Maneuver: {list: 'classManeuvers', feature: 'classManeuverFeature'},
	ArcaneShot: {list: 'classArcaneShots', feature: 'classArcaneShotFeature'},
	WildMagic: {list: 'classWildMagic', feature: 'classWildMagicFeature'},
};

export function classSections(heroClass: Pick<Class, 'sections'>): ClassSection[] {
	return Array.isArray(heroClass.sections) ? heroClass.sections : [];
}

export const SUB_CLASS_KINDS: Record<string, string> = {
	barbarian: 'Первобытные пути',
	bard: 'Коллегии',
	cleric: 'Божественные домены',
	druid: 'Круги',
	fighter: 'Воинские архетипы',
	monk: 'Монашеские традиции',
	paladin: 'Священные клятвы',
	ranger: 'Архетипы следопытов',
	rogue: 'Плутовские архетипы',
	sorcerer: 'Происхождение чар',
	warlock: 'Сверхъестественные покровители',
	wizard: 'Магические традиции',
};

/** «Подклассы барда (Коллегии)»: прежнее название подклассов класса — в скобках в конце */
export function subClassesTitle(classId: string, genitive: string): string {
	const kind = SUB_CLASS_KINDS[classId];
	return kind ? `Подклассы ${genitive} (${kind})` : `Подклассы ${genitive}`;
}

export function subClassesLabel(classId: string): string {
	const kind = SUB_CLASS_KINDS[classId];
	return kind ? `Подклассы (${kind})` : 'Подклассы';
}

/** «воина» — для «Подклассы воина»; если падежа нет, остаётся название */
export function classGenitive(heroClass: Pick<Class, 'title' | 'cases'>): string {
	return heroClass.cases?.genitive || heroClass.title;
}

function abilities(list?: string[] | null, word = 'и'): string {
	return humanEnumeration((list || []).map((ability) => Abilities[ability] || ability), word);
}

/** «Класс Воин: кость хитов к10, основная характеристика Сила или Ловкость, спасброски Сила и Выносливость.» */
export function classLead(heroClass: Class): string {
	const details = [
		heroClass.hitDie && `кость хитов к${heroClass.hitDie}`,
		heroClass.primaryAbility?.length && `основная характеристика ${abilities(heroClass.primaryAbility, 'или')}`,
		heroClass.saves?.length && `спасброски ${abilities(heroClass.saves)}`,
	].filter(Boolean);
	return `Класс ${heroClass.title}${details.length ? `: ${details.join(', ')}` : ''}.`;
}

export function classDescription(heroClass: Class): string {
	return composeDescription(classLead(heroClass), heroClass.shortDescription);
}

export function classOgCard(heroClass: Class): OgCardData {
	return {
		label: 'Класс',
		title: heroClass.title,
		originalTitle: heroClass.originalTitle,
		subtitle: [heroClass.hitDie && `Кость хитов к${heroClass.hitDie}`, abilities(heroClass.primaryAbility, 'или')]
			.filter(Boolean)
			.join(' · '),
	};
}

/** «Подкласс Мастер боевых искусств класса Воин. Умения: … » */
export function subClassDescription(subClass: SubClass): string {
	const features = (subClass.features || []).filter((feature) => !feature.parentId).map((feature) => feature.title);
	const lead = `Подкласс ${subClass.title} класса ${subClass.classTitle}.${
		features.length ? ` Умения: ${humanEnumeration(features)}.` : ''
	}`;
	return composeDescription(lead, subClass.description);
}

export function subClassOgCard(subClass: SubClass): OgCardData {
	return {
		label: 'Подкласс',
		title: subClass.title,
		originalTitle: subClass.originalTitle,
		subtitle: `Класс ${subClass.classTitle}`,
	};
}
